import React, { useState } from 'react';
import { SIMILARITY_THRESHOLDS } from '../../utils/Constants';

const scoreVariant = (score) => {
    if (score >= SIMILARITY_THRESHOLDS.DUPLICATE) return { cls: 'bg-danger', label: 'duplicate?' };
    if (score >= SIMILARITY_THRESHOLDS.RELATED) return { cls: 'bg-warning text-dark', label: 'related' };
    return { cls: 'bg-secondary', label: 'weak' };
};

const optionRow = (letter, text, correct) => (
    <div
        key={letter}
        className={`d-flex small ${correct === letter ? 'fw-bold text-success' : ''}`}
        style={{ lineHeight: 1.2 }}
    >
        <span className="me-1" style={{ minWidth: 14 }}>{letter}.</span>
        <span style={{ wordBreak: 'break-word' }}>{text || <span className="text-muted">—</span>}</span>
    </div>
);

const SimilarQuestionCard = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const variant = scoreVariant(item.similarity_score);
    // Eye-catching red treatment when the top match is very similar (> 80%).
    const highlight = item.similarity_score > SIMILARITY_THRESHOLDS.HIGHLIGHT;
    const pct = Math.round(item.similarity_score * 100);
    return (
        <div
            className="border rounded p-2 mb-2"
            style={{
                cursor: 'pointer',
                backgroundColor: highlight ? '#fef2f2' : '#fafafa',
                border: highlight ? '2px solid #dc2626' : undefined,
                boxShadow: highlight ? '0 0 0 3px rgba(220,38,38,0.15)' : undefined,
            }}
            onClick={() => setExpanded(v => !v)}
        >
            {highlight && (
                <div className="d-flex align-items-center gap-1 mb-1 fw-bold" style={{ color: '#dc2626' }}>
                    <span style={{ fontSize: '1.05rem' }}>⚠</span>
                    <span>Possible duplicate — {pct}% similar</span>
                </div>
            )}
            <div className="d-flex justify-content-between align-items-start mb-1">
                <span className={`badge ${variant.cls}`} title={`Score ${item.similarity_score.toFixed(3)}`}>
                    {item.similarity_score.toFixed(2)} · {variant.label}
                </span>
                <span className="text-muted small">#{item.question_id}</span>
            </div>
            <div
                className="mb-2 small"
                style={expanded
                    ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                    : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
                {item.question_text}
            </div>
            <div className="ps-1 mb-2">
                {['A', 'B', 'C', 'D'].map(l =>
                    optionRow(l, item[`option_${l.toLowerCase()}`], item.correct_option))}
            </div>
            <div className="d-flex flex-wrap gap-1">
                {item.level && <span className="badge bg-light text-dark">{item.level}</span>}
                {item.subject && <span className="badge bg-info-subtle text-dark">{item.subject}</span>}
                {item.topic && <span className="badge bg-warning-subtle text-dark">{item.topic}</span>}
                {item.exam && <span className="badge bg-danger-subtle text-dark">{item.exam}</span>}
                {item.difficulty && <span className="badge bg-secondary-subtle text-dark">{item.difficulty}</span>}
                {item.year && <span className="badge bg-light text-dark">{item.year}</span>}
            </div>
            {expanded && item.answer_explanation && (
                <div className="mt-2 small text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                    <strong>Explanation:</strong> {item.answer_explanation}
                </div>
            )}
        </div>
    );
};

// Compact single-card summary shown at the bottom of the left column. Only the single most
// similar EXISTING question is shown; a red banner flags a likely duplicate (> 80% similar).
// `currentQuestionId` (the question being edited/reviewed) is shown for context and defensively
// filtered out so a question is never listed as similar to itself. Refresh via "Re-fetch".
const SimilarQuestionsPanel = ({ loading, items, hasFetched, currentQuestionId = null }) => {
    if (!hasFetched) {
        return (
            <div className="text-muted small p-2">
                Pick a level, then click <em>Re-fetch Relevant Questions</em> to load the most similar question already in the bank.
            </div>
        );
    }
    if (loading) {
        return <div className="text-muted small p-2">Loading similar question…</div>;
    }
    // Never show the current question against which similarity is measured.
    const others = (items || []).filter(it => it.question_id !== currentQuestionId);
    if (others.length === 0) {
        return (
            <div className="text-muted small p-2">
                No similar question found at this level. Click <em>Re-fetch Relevant Questions</em> after editing to refresh.
            </div>
        );
    }
    const top = others[0];
    return (
        <div>
            <div className="text-muted small mb-2">
                Most similar existing question{currentQuestionId != null ? <> to the current question (#{currentQuestionId})</> : null} (matched on question + 4 options).
            </div>
            <SimilarQuestionCard key={top.question_id} item={top} />
        </div>
    );
};

export default SimilarQuestionsPanel;
